import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { useCurrentUser, useDeleteAccount, useUpdateProfile } from '../hooks/useAuth';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/ToastProvider';
import { getApiErrorMessage } from '../utils/errors';
import { formatDate } from '../utils/datetime';
import { displayName, initialsOf } from '../utils/user';

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  facebook: 'Facebook',
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
      gap: { xs: 0.5, sm: 2 },
      alignItems: 'center',
      py: 2,
    }}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

const ProfilePage: React.FC = () => {
  const { data: user, isLoading, isError, error, refetch } = useCurrentUser();
  const updateProfile = useUpdateProfile();
  const { showToast } = useToast();
  const deleteAccount = useDeleteAccount();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const openNameDialog = () => {
    setNameDraft(user?.full_name ?? '');
    updateProfile.reset();
    setNameDialogOpen(true);
  };

  const saveName = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate(
      { full_name: nameDraft.trim() },
      {
        onSuccess: () => {
          setNameDialogOpen(false);
          showToast('Name updated', 'success');
        },
      }
    );
  };

  const signInMethods = user
    ? [
        ...(user.has_password ? ['Password'] : []),
        ...user.social_accounts.map((p) => PROVIDER_LABELS[p] ?? p),
      ]
    : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 760, width: '100%', mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5">Profile</Typography>
        <Typography variant="body2" color="text.secondary">
          Your account details
        </Typography>
      </Box>

      {isError ? (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
        >
          {getApiErrorMessage(error, 'Could not load your profile.')}
        </Alert>
      ) : isLoading || !user ? (
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3.5 }} />
      ) : (
        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24, fontWeight: 700 }}>
                {initialsOf(user)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
                  {displayName(user)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {user.email}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ mt: 2 }} />

            <DetailRow label="Full name">
              <Stack direction="row" spacing={1} alignItems="center">
                {user.full_name ? (
                  <Typography sx={{ wordBreak: 'break-word' }}>{user.full_name}</Typography>
                ) : (
                  <Typography color="text.secondary">Not set</Typography>
                )}
                <Tooltip title="Edit name">
                  <IconButton size="small" onClick={openNameDialog} aria-label="Edit name">
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </DetailRow>
            <Divider />

            <DetailRow label="Email">
              <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ wordBreak: 'break-all' }}>{user.email}</Typography>
                {user.is_email_verified ? (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    icon={<VerifiedRoundedIcon />}
                    label="Verified"
                  />
                ) : (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<ErrorOutlineRoundedIcon />}
                    label="Not verified"
                  />
                )}
              </Stack>
            </DetailRow>
            <Divider />

            <DetailRow label="Sign-in methods">
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {signInMethods.map((method) => (
                  <Chip key={method} size="small" label={method} />
                ))}
              </Stack>
            </DetailRow>
            <Divider />

            <DetailRow label="Member since">
              <Typography>{formatDate(user.date_joined)}</Typography>
            </DetailRow>
          </CardContent>
        </Card>
      )}

      {user && (
        <Card sx={{ mt: 3, borderColor: 'error.light' }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Typography variant="subtitle1" color="error" gutterBottom>
              Delete account
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 440 }}>
                Permanently delete your account and all your tasks, categories, time logs and AI conversations.
                This can't be undone.
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverRoundedIcon />}
                onClick={() => {
                  deleteAccount.reset();
                  setDeleteOpen(true);
                }}
                sx={{ flexShrink: 0 }}
              >
                Delete my account
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete your account?"
        message={
          <>
            This permanently deletes <strong>{user?.email}</strong> and everything in it: tasks, categories,
            time logs and AI conversations. You will be signed out, and the data can't be recovered.
          </>
        }
        confirmText="delete"
        confirmLabel="Delete my account"
        loading={deleteAccount.isPending}
        error={deleteAccount.isError ? getApiErrorMessage(deleteAccount.error, 'Could not delete your account.') : null}
        onConfirm={() => deleteAccount.mutate('delete')}
        onCancel={() => setDeleteOpen(false)}
      />

      <Dialog open={nameDialogOpen} onClose={() => setNameDialogOpen(false)} fullWidth maxWidth="xs">
        <form onSubmit={saveName} noValidate>
          <DialogTitle>Edit name</DialogTitle>
          <DialogContent>
            {updateProfile.isError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {getApiErrorMessage(updateProfile.error, 'Could not update your name.')}
              </Alert>
            )}
            <TextField
              label="Full name"
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              fullWidth
              autoFocus
              autoComplete="name"
              inputProps={{ maxLength: 255 }}
              helperText="Leave empty to remove your name."
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setNameDialogOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={updateProfile.isPending || nameDraft.trim() === (user?.full_name ?? '')}
            >
              {updateProfile.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProfilePage;
